import { Box, SxProps, Tooltip, Typography, Chip } from '@mui/material';
import FundInvestmentInvestor from 'FundInvestmentData';
import { Permissions } from 'Permission';
import React from 'react';
import FundOpportunity from '../../../models/FundOpportunity';
import ExtendedLiquid from '../../../services/ExtendedLiquid';
import { Palette } from 'Palette';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../../styles/StyledButtons';
import { chartColours } from '../../template/ChartRenderer';
import { useGlobalConfig } from '../../../context/GlobalConfigContext';

// This standalone page is fully superseded by the embedded Cashflow Reports
// section on Fund Management — hidden rather than deleted in case it needs
// to come back.
const SHOW_STANDALONE_CASHFLOW_REPORTS_BUTTON = false;

const CASHFLOW_REPORTS_REMINDER_SX = {
  backgroundColor: '#EC4899',
  color: '#FFFFFF',
  '@media (hover: hover)': {
    '&:hover': { backgroundColor: '#DB2777', color: '#FFFFFF' },
  },
};

interface ActionBarProps {
  fundOpportunity: FundOpportunity;
  hasPermission: (permissionKey: Permissions) => boolean;
  loaded: boolean;
  data?: {
    opportunity: FundOpportunity;
    investors: FundInvestmentInvestor[];
  };
  onCloneModalOpen: () => void;
  onRequestReview: () => void;
  onNavigate: (path: string) => void;
  sx?: SxProps;
}

const ActionBar: React.FC<ActionBarProps> = ({
  fundOpportunity,
  hasPermission,
  loaded,
  data,
  onCloneModalOpen,
  onNavigate,
  sx,
}) => {
  const { config } = useGlobalConfig();

  const renderChartSection = () => {
    const colours = chartColours();

    if (!loaded || !data) {
      return null;
    }

    // Calculate investments using the same logic as currentInvestment function
    const totalCommitted = data.investors.reduce((sum, i) => {
      if (i.status === 'COMMITTED_INVESTOR') {
        const amount = i.committedInvestment;
        return sum + (amount ? (typeof amount === 'number' ? amount : parseInt(amount)) : 0);
      }
      return sum;
    }, 0);

    const totalPreferred = data.investors.reduce((sum, i) => {
      if (i.status === 'OPPORTUNITY') {
        const amount = i.preferredInvestment;
        return sum + (amount ? (typeof amount === 'number' ? amount : parseInt(amount)) : 0);
      }
      return sum;
    }, 0);

    const totalEOI = data.investors.reduce((sum, i) => {
      if (i.status === 'LEAD' && i.eoiSubmitted) {
        const amount = i.indicativeInvestment;
        return sum + (amount ? (typeof amount === 'number' ? amount : parseInt(amount)) : 0);
      }
      return sum;
    }, 0);

    const totalInvestment = totalCommitted + totalPreferred + totalEOI;
    const targetSize = fundOpportunity.fundSize || totalInvestment;

    // Calculate percentages with safeguards to prevent overflow
    const committedPercentage = Math.min((totalCommitted / targetSize) * 100, 100);
    const preferredPercentage = Math.min((totalPreferred / targetSize) * 100, 100);
    const eoiPercentage = Math.min((totalEOI / targetSize) * 100, 100);

    // Ensure total percentage doesn't exceed 100%
    const totalPercentage = committedPercentage + preferredPercentage + eoiPercentage;
    const scaleFactor = totalPercentage > 100 ? 100 / totalPercentage : 1;

    const scaledCommittedPercentage = committedPercentage * scaleFactor;
    const scaledPreferredPercentage = preferredPercentage * scaleFactor;
    const scaledEoiPercentage = eoiPercentage * scaleFactor;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '24px',
            backgroundColor: Palette.tone3.background,
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* EOI Investment (bottom layer) */}
          <Tooltip title={`EOI Investment: ${ExtendedLiquid.currencyMillions(totalEOI, true)}`}>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                bottom: 0,
                height: '100%',
                width: `${scaledEoiPercentage}%`,
                backgroundColor: colours[0],
                borderRadius: '12px 0 0 12px',
                transition: 'width 0.3s ease',
              }}
            />
          </Tooltip>

          {/* Preferred Investment (middle layer) */}
          <Tooltip
            title={`Preferred Investment: ${ExtendedLiquid.currencyMillions(totalPreferred, true)}`}
          >
            <Box
              sx={{
                position: 'absolute',
                left: `${scaledEoiPercentage}%`,
                bottom: 0,
                height: '100%',
                width: `${scaledPreferredPercentage}%`,
                backgroundColor: colours[1],
                transition: 'width 0.3s ease',
              }}
            />
          </Tooltip>

          {/* Committed Investment (top layer) */}
          <Tooltip
            title={`Committed Investment: ${ExtendedLiquid.currencyMillions(totalCommitted, true)}`}
          >
            <Box
              sx={{
                position: 'absolute',
                left: `${scaledEoiPercentage + scaledPreferredPercentage}%`,
                bottom: 0,
                height: '100%',
                width: `${scaledCommittedPercentage}%`,
                backgroundColor: colours[2],
                borderRadius: '0 12px 12px 0',
                transition: 'width 0.3s ease',
              }}
            />
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 500,
              color: Palette.tone2.text,
            }}
          >
            {ExtendedLiquid.currencyMillions(totalInvestment, true)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 500,
              color: Palette.tone2.text,
              opacity: 0.7,
            }}
          >
            {ExtendedLiquid.currencyMillions(targetSize, true)}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderDelayChip = () => {
    if (!config) return null;

    const delayMinutes = config.pushNotificationDelayMinutes || 0;
    const isZeroDelay = delayMinutes === 0;
    const textColor = isZeroDelay ? Palette.warning.text : Palette.tone1.activeText;
    const background = isZeroDelay ? Palette.warning.background : Palette.tone1.activeBg;
    const hoverBackground = isZeroDelay ? Palette.warning.activeBg : Palette.tone2.activeBg;
    const borderColor = isZeroDelay ? Palette.warning.border : Palette.default.border;

    return (
      <Tooltip title="Current push notification delay for investor promotions. Click to configure.">
        <Chip
          label={`${delayMinutes} minute${delayMinutes === 1 ? '' : 's'} delay`}
          size="small"
          onClick={() => {
            window.open('/admin/globalConfig#pushNotificationDelayMinutes', '_blank');
          }}
          sx={{
            cursor: 'pointer',
            height: '34px',
            backgroundColor: background,
            color: textColor,
            border: `1px solid ${borderColor}`,
            '& .MuiChip-label': { px: '10px' },
            '&:hover': {
              backgroundColor: hoverBackground,
            },
          }}
        />
      </Tooltip>
    );
  };

  const chartSection = renderChartSection();
  const delayChip = renderDelayChip();

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch', flexWrap: 'wrap', ...sx }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <StyledButton
            noTopSpacing
            onClick={() => onNavigate(`/opportunities/${fundOpportunity.id}/content`)}
          >
            Fund Information
          </StyledButton>

          <StyledButton
            noTopSpacing
            onClick={() => onNavigate(`/opportunities/${fundOpportunity.id}/fundUpdate`)}
          >
            Fund Management
          </StyledButton>

          {SHOW_STANDALONE_CASHFLOW_REPORTS_BUTTON && (
            <StyledButton
              noTopSpacing
              sx={CASHFLOW_REPORTS_REMINDER_SX}
              onClick={() => onNavigate(`/opportunities/${fundOpportunity.id}/irrManagement`)}
            >
              Cashflow Reports
            </StyledButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {hasPermission(Permissions.FundOpportunityCreation) && (
            <StyledButton
              noTopSpacing
              buttonVariant="inactiveOrCancel"
              onClick={onCloneModalOpen}
              sx={SECONDARY_BUTTON_HOVER_SX}
            >
              Clone Opportunity
            </StyledButton>
          )}

          <StyledButton
            noTopSpacing
            buttonVariant="inactiveOrCancel"
            onClick={() => onNavigate(`/activityLogs/FUND_OPPORTUNITY/${fundOpportunity.id}`)}
            sx={SECONDARY_BUTTON_HOVER_SX}
          >
            Activity Log
          </StyledButton>
        </Box>
      </Box>

      {(chartSection || delayChip) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 1.5,
            ml: 'auto',
            minWidth: '320px',
            maxWidth: '460px',
            flex: 1,
          }}
        >
          {chartSection}
          {delayChip}
        </Box>
      )}
    </Box>
  );
};

export default ActionBar;
